import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const [themeColor, setThemeColor] = useState('#2563eb');

  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') {
      setThemeColor('#2563eb');
      return;
    }
    const handleColorChange = (e) => {
      if (e.detail && e.detail.color) {
        setThemeColor(e.detail.color);
      }
    };
    window.addEventListener('hero-color-change', handleColorChange);
    return () => window.removeEventListener('hero-color-change', handleColorChange);
  }, [location.pathname]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const serviceMenu = [
    {
      title: "Pôles Spécialisés",
      items: [
        { path: '/real-estate-portal', label: 'Immobilier & Logement', icon: 'Home', desc: 'Vente, location et gestion' },
        { path: '/vehicle-rental', label: 'Location de Véhicules', icon: 'Car', desc: 'Flotte diverse et chauffeurs' },
      ]
    },
    {
      title: "Services Généraux",
      items: [
        { path: '/services-overview', label: 'IT & Réseaux', icon: 'Wifi', desc: 'Infrastructure et maintenance' },
        { path: '/services-overview', label: 'BTP & Maintenance', icon: 'Hammer', desc: 'Construction et rénovation' },
        { path: '/services-overview', label: 'Logistique & Divers', icon: 'Truck', desc: 'Fournitures et transport' },
      ]
    }
  ];

  const handleMouseEnter = (menu) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  const isActive = (path) => location.pathname === path;
  const activeStyle = { color: themeColor };
  const bgStyle = { backgroundColor: themeColor };

  return (
    <>
      {/* Top Decoration Line */}
      <div
        className="fixed top-0 left-0 w-full h-1 z-[60] transition-colors duration-1000"
        style={{
          background: `linear-gradient(90deg, ${themeColor} 0%, #ffffff 50%, ${themeColor} 100%)`,
          opacity: 0.8
        }}
      />

      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled
          ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-lg py-3'
          : 'bg-white dark:bg-slate-900 py-5 border-b border-slate-100 dark:border-slate-800'
        }`}
      >
        <div className="w-full mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-between items-center">

            {/* ═══════ LOGO ═══════ */}
            <Link to="/" className="flex items-center group" onClick={() => setActiveDropdown(null)}>
              <img
                src="/assets/images/logo.svg"
                alt="ORAZEK Investment"
                className="h-12 w-auto mr-3 rounded-lg transition-all duration-300 group-hover:scale-105"
              />
            </Link>

            {/* ═══════ DESKTOP NAV ═══════ */}
            <nav className="hidden lg:flex items-center gap-10">
              <Link
                to="/"
                className="text-base font-bold transition-colors duration-300 hover:opacity-80 py-2"
                style={isActive('/') ? activeStyle : { color: isDark ? '#cbd5e1' : '#334155' }}
              >
                Accueil
              </Link>

              {/* Dropdown Services */}
              <div
                className="relative"
                onMouseEnter={() => handleMouseEnter('services')}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className="flex items-center text-base font-bold transition-colors duration-300 hover:opacity-80 py-2"
                  style={['/services-overview', '/real-estate-portal', '/vehicle-rental'].includes(location.pathname) ? activeStyle : { color: isDark ? '#cbd5e1' : '#334155' }}
                >
                  <span>Nos Services</span>
                  <Icon name="ChevronDown" size={20} className={`ml-1.5 transition-transform duration-200 ${activeDropdown === 'services' ? 'rotate-180' : ''}`} />
                </button>

                {activeDropdown === 'services' && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-5 w-[660px] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden grid grid-cols-2 p-4 animate-in fade-in slide-in-from-top-5 duration-200">
                    {serviceMenu.map((category, idx) => (
                      <div key={idx} className="p-5 space-y-4">
                        <h3
                          className="text-sm font-extrabold uppercase tracking-wider border-b border-slate-100 dark:border-slate-700 pb-3"
                          style={{ color: themeColor }}
                        >
                          {category.title}
                        </h3>
                        <ul className="space-y-1">
                          {category.items.map((item, itemIdx) => (
                            <li key={itemIdx}>
                              <Link
                                to={item.path}
                                className="group/item flex items-center gap-4 p-3.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors"
                                onClick={() => setActiveDropdown(null)}
                              >
                                <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 group-hover/item:text-blue-500 transition-colors flex-shrink-0">
                                  <Icon name={item.icon} size={20} />
                                </div>
                                <div>
                                  <span className="block text-[15px] font-bold text-slate-800 dark:text-slate-100 group-hover/item:text-blue-600 dark:group-hover/item:text-blue-400 transition-colors">
                                    {item.label}
                                  </span>
                                  <span className="block text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                    {item.desc}
                                  </span>
                                </div>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <div className="col-span-2 bg-slate-50 dark:bg-slate-900 p-4 text-center border-t border-slate-100 dark:border-slate-700">
                      <Link
                        to="/services-overview"
                        onClick={() => setActiveDropdown(null)}
                        className="text-sm font-bold hover:underline"
                        style={{ color: themeColor }}
                      >
                        Voir tous nos services &rarr;
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/news-articles-hub"
                className="text-base font-bold transition-colors hover:opacity-80 py-2"
                style={isActive('/news-articles-hub') ? activeStyle : { color: isDark ? '#cbd5e1' : '#334155' }}
              >
                Actualités
              </Link>

              <Link
                to="/about-orazek"
                className="text-base font-bold transition-colors hover:opacity-80 py-2"
                style={isActive('/about-orazek') ? activeStyle : { color: isDark ? '#cbd5e1' : '#334155' }}
              >
                À Propos
              </Link>

              <Link
                to="/contact"
                className="text-base font-bold transition-colors hover:opacity-80 py-2"
                style={isActive('/contact') ? activeStyle : { color: isDark ? '#cbd5e1' : '#334155' }}
              >
                Contact
              </Link>
            </nav>

            {/* ═══════ DESKTOP ACTIONS ═══════ */}
            <div className="hidden lg:flex items-center gap-5">
              <a
                href="tel:+243818883531"
                className="hidden xl:flex items-center text-slate-600 dark:text-slate-300 transition-colors text-[15px] font-semibold hover:opacity-80"
              >
                <Icon name="Phone" size={18} className="mr-2.5" />
                <span>+243 81 888 3531</span>
              </a>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2.5 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all duration-300 border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                title={isDark ? 'Mode clair' : 'Mode sombre'}
              >
                <Icon name={isDark ? 'Sun' : 'Moon'} size={22} />
              </button>

              {user ? (
                <div className="flex items-center gap-4 pl-5 border-l border-slate-200 dark:border-slate-700">
                  {userProfile?.role === 'admin' && (
                    <Link to="/protected-admin-dashboard">
                      <button className="flex items-center px-6 py-2.5 text-[15px] font-bold text-white bg-slate-900 dark:bg-slate-700 rounded-xl hover:bg-slate-800 dark:hover:bg-slate-600 transition-all shadow-md hover:shadow-lg">
                        <Icon name="LayoutDashboard" size={18} className="mr-2.5" />
                        Tableau de bord
                      </button>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-xl transition-all"
                    title="Déconnexion"
                  >
                    <Icon name="LogOut" size={22} />
                  </button>
                </div>
              ) : (
                <Link to="/admin-login">
                  <button
                    className="flex items-center px-7 py-2.5 text-[15px] font-bold text-white rounded-xl transition-all shadow-md hover:shadow-lg hover:brightness-110 duration-1000"
                    style={bgStyle}
                  >
                    <Icon name="LogIn" size={18} className="mr-2.5" />
                    Connexion
                  </button>
                </Link>
              )}
            </div>

            {/* ═══════ MOBILE BURGER ═══════ */}
            <button
              className="lg:hidden p-2.5 text-slate-600 dark:text-slate-300 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={{ color: isMobileMenuOpen ? undefined : themeColor }}
            >
              <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={32} />
            </button>
          </div>
        </div>
      </header>

      {/* ═══════ MOBILE MENU ═══════ */}
      <div className={`fixed inset-0 z-[100] transform transition-transform duration-300 lg:hidden ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />

        <div className="absolute right-0 top-0 h-full w-[90%] max-w-md bg-white dark:bg-slate-900 shadow-2xl flex flex-col">
          {/* Mobile Header */}
          <div className="px-7 py-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
            <div className="flex items-center">
              <img
                src="/assets/images/logo.svg"
                alt="ORAZEK Investment"
                className="h-10 w-auto rounded-lg"
              />
            </div>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-3 text-slate-500 dark:text-slate-400 hover:text-red-500 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all">
              <Icon name="X" size={28} />
            </button>
          </div>

          {/* Mobile Navigation */}
          <div className="flex-1 overflow-y-auto py-6 px-6 space-y-2">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center text-[17px] font-bold text-slate-900 dark:text-white py-4 px-5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <Icon name="Home" size={22} className="mr-4 text-slate-400" />
              Accueil
            </Link>

            <div className="space-y-2 pt-4">
              <div className="text-xs font-extrabold text-slate-400 uppercase tracking-[0.15em] px-5 mb-3">Nos Services</div>
              <Link to="/services-overview" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center py-4 px-5 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white font-bold text-[17px]">
                <Icon name="Briefcase" size={22} className="mr-4" style={{ color: themeColor }} />
                Tous les Services
              </Link>
              <Link to="/real-estate-portal" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center py-4 px-5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-[17px] font-semibold transition-colors">
                <Icon name="Home" size={22} className="mr-4" style={{ color: themeColor }} />
                Immobilier
              </Link>
              <Link to="/vehicle-rental" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center py-4 px-5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 text-[17px] font-semibold transition-colors">
                <Icon name="Car" size={22} className="mr-4" style={{ color: themeColor }} />
                Location Véhicules
              </Link>
            </div>

            <div className="pt-5 border-t border-slate-100 dark:border-slate-800 space-y-2 mt-3">
              <Link to="/news-articles-hub" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center text-[17px] font-bold text-slate-900 dark:text-white py-4 px-5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                <Icon name="Newspaper" size={22} className="mr-4 text-slate-400" />
                Actualités
              </Link>
              <Link to="/about-orazek" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center text-[17px] font-bold text-slate-900 dark:text-white py-4 px-5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                <Icon name="Info" size={22} className="mr-4 text-slate-400" />
                À Propos
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center text-[17px] font-bold text-slate-900 dark:text-white py-4 px-5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                <Icon name="Mail" size={22} className="mr-4 text-slate-400" />
                Contact
              </Link>
            </div>

            {/* Mobile Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="flex items-center w-full py-4 px-5 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 mt-5 text-[17px] font-semibold border border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-500 transition-all"
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={22} className="mr-4" style={{ color: themeColor }} />
              <span>{isDark ? 'Mode clair' : 'Mode sombre'}</span>
            </button>
          </div>

          {/* Mobile Footer */}
          <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950">
            {user ? (
              <>
              {userProfile?.role === 'admin' && (
              <Link to="/protected-admin-dashboard" onClick={() => setIsMobileMenuOpen(false)}>
                <button className="w-full flex justify-center items-center px-5 py-4 text-[17px] font-bold text-white bg-slate-900 dark:bg-slate-700 rounded-xl mb-4">
                  <Icon name="LayoutDashboard" size={22} className="mr-3" />
                  Tableau de bord
                </button>
              </Link>
              )}
              <button
                onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                className="w-full flex justify-center items-center px-5 py-4 text-[17px] font-bold text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400 rounded-xl mb-4"
              >
                <Icon name="LogOut" size={22} className="mr-3" />
                Déconnexion
              </button>
              </>
            ) : (
              <Link to="/admin-login" onClick={() => setIsMobileMenuOpen(false)}>
                <button
                  className="w-full flex justify-center items-center px-5 py-4 text-[17px] font-bold text-white rounded-xl shadow-md mb-4 transition-colors duration-1000"
                  style={bgStyle}
                >
                  <Icon name="LogIn" size={22} className="mr-3" />
                  Connexion Staff
                </button>
              </Link>
            )}
            <div className="text-center text-sm text-slate-400 dark:text-slate-500 font-medium">
              Version 2.0 • Orazek App
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
